// Surface 2 — one product: its rate against benchmark, the full recommendation
// with its evidence, and its change history.
import { Link } from 'react-router-dom';
import React from 'react';
import { FiX } from 'react-icons/fi';
import s from '../CTROptimizer.module.css';
import { money, num, pct } from '../format';

const Cause = ({ cause, asin }) => (
  <div className={s.section}>
    <div className={s.sectionTitle}>
      {cause.cause}
      <span className={cause.strength === 'direct' ? s.direct : s.assessed}>
        {' '}{cause.strength === 'direct' ? 'measured' : 'assessed'}
      </span>
    </div>
    <p className={s.hint} style={{ color: '#4a4a4a' }}>{cause.headline}</p>
    {cause.evidence && (
      <div className={s.chipRow}>
        {Object.entries(cause.evidence).map(([k, v]) => (
          <span key={k} className={s.chip}>
            {k.replace(/_/g, ' ')}:{' '}
            {typeof v === 'number' && k.includes('price') ? money(v)
              : typeof v === 'number' && (k.includes('share') || k === 'ratio')
                ? (k === 'ratio' ? `${v.toFixed(2)}×` : pct(v, 0))
                : String(v)}
          </span>
        ))}
      </div>
    )}
    {cause.action && <p className={s.hint} style={{ marginTop: 6 }}>{cause.action}</p>}
    <FixLink cause={cause.cause} asin={asin} />
  </div>
);

// Where the fix is actually made.
//
// Only two of the causes are fixed in a tool we have. Price is set in Seller
// Central and the delivery promise is a fulfilment decision — offering a link
// to a listing editor for either would send the seller to redo work that was
// never the problem, which is the one thing the recommendation panel exists to
// prevent. So those two say what they are instead of pretending to a button.
const FIXES = {
  title: { to: '/dashboard/generate/single', label: 'Rewrite the title' },
  'main image': { to: '/dashboard/tools/image-zip-upload', label: 'Replace the main image' },
};
const NOT_A_LISTING_FIX = {
  'delivery promise': 'Fixed in fulfilment, not in the listing — faster shipping on this '
    + 'product, not a rewrite.',
  price: 'Set in Seller Central. Nothing in the listing content will recover these clicks.',
};

function FixLink({ cause, asin }) {
  const fix = FIXES[cause];
  if (fix) {
    return (
      <Link className={s.fixLink} to={`${fix.to}?asin=${encodeURIComponent(asin || '')}`}>
        {fix.label} →
      </Link>
    );
  }
  const note = NOT_A_LISTING_FIX[cause];
  return note ? <p className={s.fixNote}>{note}</p> : null;
}

export default function ProductDetail({ data, onClose }) {
  if (!data) return null;
  const d = data.diagnosis || {};
  const asin = data.asin;
  // The terms behind this product. A seller told a listing loses clicks asks
  // which queries next, and that answer lives in Search Term Performance —
  // the same stored rows, so the figures agree.
  const termsLink = `/dashboard/seller-intelligence/term-performance?asins=${encodeURIComponent(asin)}`;

  return (
    <>
      <div className={s.backdrop} onClick={onClose} />
      <aside className={s.drawer}>
        <div className={s.drawerHead}>
          <div>
            <div className={s.drawerTitle}>{data.asin}</div>
            <div className={s.hint}>
              {num(data.impressions)} search impressions · {num(data.clicks)} clicks
              {data.product_type && ` · ${data.product_type}`}
            </div>
          </div>
          <button className={s.close} onClick={onClose}><FiX /></button>
        </div>

        <div className={s.drawerBody}>
          <div className={s.stats}>
            <div className={s.stat}>
              <span className={s.statValue}>{pct(data.search_click_rate)}</span>
              <span className={s.statLabel}>search click rate</span>
            </div>
            <div className={s.stat}>
              <span className={s.statValue}>{pct(data.benchmark)}</span>
              <span className={s.statLabel}>benchmark · {data.benchmark_source || '—'}</span>
            </div>
            <div className={s.stat}>
              <span className={s.statValue}>{num(data.clicks_lost)}</span>
              <span className={s.statLabel}>clicks lost</span>
            </div>
          </div>

          <p className={s.hint} style={{ margin: '4px 0 14px' }}>
            This is the click rate in Amazon <strong>search results</strong> only — not
            browse, not recommendations, not advertising. An advertising click rate measures
            something else and the two will not agree.
          </p>

          {!!data.points?.length && (
            <div className={s.section}>
              <div className={s.sectionTitle}>Click rate against benchmark, by window</div>
              <p className={s.hint}>
                A rate that slid gradually and one that dropped at a point in time are
                different problems. The benchmark travels alongside — a gap that widened
                while the benchmark rose is the market moving, not the listing failing.
              </p>
              <div className={s.tableWrap}>
                <table className={s.table}>
                  <thead>
                    <tr>
                      <th>Window</th><th className={s.numeric}>Impressions</th>
                      <th className={s.numeric}>Click rate</th>
                      <th className={s.numeric}>Benchmark</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.points.map((pt) => (
                      <tr key={pt.start_date}>
                        <td>{pt.start_date}</td>
                        {!pt.collected ? (
                          <td colSpan={3} className={s.suppressedRow}>not collected</td>
                        ) : pt.search_click_rate == null ? (
                          <>
                            <td className={s.numeric}>{num(pt.impressions)}</td>
                            <td colSpan={2} className={s.suppressedRow}>{pt.note}</td>
                          </>
                        ) : (
                          <>
                            <td className={s.numeric}>{num(pt.impressions)}</td>
                            <td className={s.numeric}>{pct(pt.search_click_rate)}</td>
                            <td className={s.numeric}>{pct(pt.benchmark)}</td>
                          </>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {data.movement && (
                <p className={s.hint} style={{ marginTop: 8 }}>
                  {data.movement.available
                    ? <>Against {data.movement.compared_to}: {data.movement.reads_as}
                      {' '}({data.movement.rate_change >= 0 ? '+' : ''}
                      {(data.movement.rate_change * 100).toFixed(2)}pp).</>
                    : <>No movement yet — {data.movement.reason}.</>}
                </p>
              )}
            </div>
          )}

          {d.state === 'suppressed' && (
            <div className={s.empty}>{d.reason}</div>
          )}
          {d.state === 'healthy' && (
            <div className={s.empty}>
              Nothing to fix — this product is {d.reason}.
            </div>
          )}
          {d.state === 'undetermined' && (
            <div className={s.section}>
              <div className={s.sectionTitle}>
                Undetermined <span className={s.undetermined}>no cause named</span>
              </div>
              <p className={s.hint}>{d.reason} Learning the problem is not your listing is
                itself useful — a fabricated cause would send you to redo work that was
                fine.</p>
              {!!d.not_checked?.length && (
                <>
                  <div className={s.sectionTitle} style={{ marginTop: 10 }}>
                    What could not be checked
                  </div>
                  <ul className={s.cardList}>
                    {d.not_checked.map((x) => <li key={x} className={s.cardNote}>{x}</li>)}
                  </ul>
                </>
              )}
            </div>
          )}
          <Link className={s.fixLink} to={termsLink} style={{ marginBottom: 12 }}>
            See the search terms this product competes on →
          </Link>
          {d.state === 'diagnosed' && d.causes.map((c) => (
            <Cause key={c.cause} cause={c} asin={asin} />
          ))}

          <div className={s.section}>
            <div className={s.sectionTitle}>Change history</div>
            {!data.change_history_available ? (
              <p className={s.hint}>
                No change history for this product. Image, title and price changes are only
                measurable where the platform that made them recorded when they happened —
                Amazon publishes no such history.
              </p>
            ) : (
              <div className={s.tableWrap}>
                <table className={s.table}>
                  <thead>
                    <tr>
                      <th>Date</th><th>What changed</th>
                      <th className={s.numeric}>Before</th><th className={s.numeric}>After</th>
                      <th className={s.numeric}>Difference</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.changes.map((c, i) => (
                      <tr key={`${c.date}-${i}`}>
                        <td>{c.date}</td>
                        <td>{c.what}</td>
                        <td className={s.numeric}>{pct(c.before)}</td>
                        <td className={s.numeric}>{pct(c.after)}</td>
                        <td className={s.numeric}>
                          {c.difference == null
                            ? <span className={s.undetermined}>{c.state}</span>
                            : <span className={c.difference >= 0 ? s.up : s.down}>
                                {c.difference >= 0 ? '+' : ''}{(c.difference * 100).toFixed(2)}pp
                              </span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </aside>
    </>
  );
}
